import React from "react"
import styled from "styled-components"
import { up } from "styled-breakpoints"
import colors from "@constants/colors"
import { columns14 } from "@constants/layout"
import measurements from "@constants/measurements"
import { smallcaps, h3, h4 } from "@constants/typography"
import CategoryCard, { CategoryCardProps } from "@components/CategoryCard"
import Scrollbar, {
  Container as ScrollbarContainer,
} from "@components/Scrollbar"
import VerticalSpace from "@components/VerticalSpace"
import { MobileOnly, LaptopUpOnly } from "@utils/MediaQueries"

const Container = styled.div`
  ${ScrollbarContainer} {
    padding: 0 20px;
  }
`

const Header = styled.div`
  padding: 0 20px;

  ${up("laptop")} {
    padding: 0 ${columns14(1)};
  }

  ${up("maxWidth")} {
    max-width: ${measurements.MAX_WIDTH};
    margin: 0 auto;
    padding: 0;
  }
`

const Title = styled.div`
  ${h4};
  margin: 0;
  color: ${colors.obsidian};

  ${up("laptop")} {
    ${h3};
    color: ${colors.obsidian};
  }
`

const Subtitle = styled.div`
  ${smallcaps}
  font-size: 16px;
  color: ${colors.obsidian};
`

const Content = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
  padding-left: 20px;

  ${up("laptop")} {
    flex-wrap: wrap;
    margin: 0 auto;
    padding: 0 ${columns14(1)};
  }

  ${up("maxWidth")} {
    max-width: ${measurements.MAX_WIDTH};
    padding: 0;
  }
`

const CardWrapper = styled.div`
  display: inline-flex;
  width: 144px;
  min-height: 176px;
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: 8px;

  ${up("laptop")} {
    margin-right: 12px;
    margin-bottom: 12px;
  }
`

const Spacer = styled.div`
  flex-shrink: 0;
  width: 10px;
  height: auto;
`

// mobile Safari PLEASE
const MobileSafariScrollbarHider = styled.div`
  z-index: 10;
  width: 100%;
  height: 20px;
  background-color: ${colors.snow};
`
interface CategoriesProps {
  categories: Array<CategoryCardProps>
  galleryLink?: string
  clearanceLink?: string
}

const galleryImage = {
  srcSet:
    "https://www.datocms-assets.com/31486/1701196498-144x144-product-icons-gallery.png?dpr=0.5&fm=png 72w,https://www.datocms-assets.com/31486/1701196498-144x144-product-icons-gallery.png?dpr=0.75&fm=png 108w,https://www.datocms-assets.com/31486/1701196498-144x144-product-icons-gallery.png?fm=png 144w",
  webpSrcSet:
    "https://www.datocms-assets.com/31486/1701196498-144x144-product-icons-gallery.png?dpr=0.5&fm=webp 72w,https://www.datocms-assets.com/31486/1701196498-144x144-product-icons-gallery.png?dpr=0.75&fm=webp 108w,https://www.datocms-assets.com/31486/1701196498-144x144-product-icons-gallery.png?fm=webp 144w",
  sizes: "(max-width: 144px) 100vw, 144px",
  src: "https://www.datocms-assets.com/31486/1701196498-144x144-product-icons-gallery.png?fm=png",
  width: 144,
  height: 144,
  aspectRatio: 1,
  alt: null,
  title: null,
  bgColor: "#b6b7b8",
  base64:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAADWElEQVR42nWWe1PUMBTF+0jbXR4yigp8Wv9w/KI6zuCgCO4Dtm0SfydNum2BzBzSTe495yb3JsF8+fotK8syq6oqq+s6q5smq4wJY2rOuazruhF932fW2sx7H+bzPB+RWphhXjbGx48ZwvxAcOyzF3ZDE3k2a3kUkahRhPpQ7+RIHxC9jmR+FJk3Hymz2SpSmwmIOPSTCOcRz8emhMttSt+jwHJ7lgRvtWkOjiL5GIx54fCG09HxGN10riiKsY+Tg8ByMiFVkdpxfLBhdfKu8CmBZaxNNsuqMiISTCzNkj59x9yUEFbq1ehVeTUwChPSjr9bbLdLkSBgImFCdfzWWi8gPBGhiOE3nAEJKHeKQEQH0ECslTwtVwFPxZ9hBTpsEQUruSKG9xCeQrgSMc4B1jrlTgFQ2f6ZhK6oCcfvH8vcGJGXi1WAa6K9xvYSnEG+grjGQcQkx5chD95bl+d7xuu8cNa58P1nelZMSOgkueATuEHgRgIYv8NppaRqa4DyUMbrosN2F8azrGdgz/cmJT2sYFHTZ+AzuOKn8BGc47x2bkh031vQF4Bz6Uhwvi7KMicpB8pgw+ofIL8dBVw8vRAU9JcCUSR84Pc7Z21DxBWkkHcFl14OJNASFostOluWW8gvsLtgQCLPQUC3oxqD572153bAGUNK7infaxsEetmKvGjbVgLoWjhybVODv4JYIbLmez0K6ApWUpRElVSvtA+nLCRSAWg7gFPUItYKENEKrCoJX6/ikL0Qz8ywRRgmAWuqqmcfOiYPOFJ+2V7JBC0CuSImB0lUW9thQ+2XO9lCqKhbQWekCCs4btEWpw34RzSnCFYM95Ds4mkmMl2CIWeKVL326InRR2z/ggeIHxnbpDvNWAT0A+KWTb3DiWoJy1SFbJiq46FSUH54YMbmdJKJVKX5myB/sb939F5bpErS0U8XmCJSTf/kIPU47sCpDhfzunM8YHPDRaiKk4BXklnxjtN/D255dg/pwAaBV55A7eV38IjBSTk0IjIOJ5fuKewFCasu9pA/NE3j9a7ruhkFXjwew5Wsi+teiPkZ76t0Z6WXTSTpHwYEQl8Bk7Zo+agUkwekiG9CutLTta5tXQokhP9MJPDaCpbPoMSy+T01gwSm4kvI5j9wsIV3l7uvNgAAAABJRU5ErkJggg==",
}

const clearanceImage = {
  srcSet:
    "https://www.datocms-assets.com/31486/1701196494-144x144-product-icons-clearance.png?dpr=0.5&fm=png 72w,https://www.datocms-assets.com/31486/1701196494-144x144-product-icons-clearance.png?dpr=0.75&fm=png 108w,https://www.datocms-assets.com/31486/1701196494-144x144-product-icons-clearance.png?fm=png 144w",
  webpSrcSet:
    "https://www.datocms-assets.com/31486/1701196494-144x144-product-icons-clearance.png?dpr=0.5&fm=webp 72w,https://www.datocms-assets.com/31486/1701196494-144x144-product-icons-clearance.png?dpr=0.75&fm=webp 108w,https://www.datocms-assets.com/31486/1701196494-144x144-product-icons-clearance.png?fm=webp 144w",
  sizes: "(max-width: 144px) 100vw, 144px",
  src: "https://www.datocms-assets.com/31486/1701196494-144x144-product-icons-clearance.png?fm=png",
  width: 144,
  height: 144,
  aspectRatio: 1,
  alt: null,
  title: null,
  bgColor: "#bebdbd",
  base64:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAMAAADXqc3KAAABv1BMVEWytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriytriAQzOPAAAAlXRSTlMAAQIEBQYHCAkKDA0ODxASExQVFhcYGRscHh8gISIjJScpKissLS4xMjY3OTo8PT4/QEFERUZHTVBRUlRVV1laW11gYWJjZGZnaWtucHV2eHt8fn+AgYKFhoiJiouOkJGTlJWXmpucnqOkpaerrK2xsrO1tre4vL3Cw8TFxsfIzM3P0dLU2drb3N3g4+Tm5+jr7/P09z/1s9oAAAD6SURBVHjabdJhc8IgDAZgU0JCKOo2z///9zY3nVVroS2w+WGTVvnE5bl74ZLgojiMPv7dsQSj4fIMXtYVp+sj1BunfDUMD7DdGMXV8D6H7dtKKoGhPc/g1bkl+BTcDFZuubaAgUXFCTgjYmEhTNRNwBorkkcmpSZRyCJs8qhRT6G2REKZUSFNwJEYohw0Kl1CZZl/Q5JCrQnyHay+1QGUQkXs73Ar5pRzTBkAi6gcxz4EiEM/jLl8o/PtpcYxXruu976AsBdLifL10DTHsfxuwwRBYtvsP0/TJu4gdia1ze4wn8dH7k3qvr4fR7uLdbocny3DOaX/JVn8AMMIZ/m+fx6EAAAAAElFTkSuQmCC",
}

const Categories: React.FC<CategoriesProps> = ({
  categories,
  galleryLink = undefined,
  clearanceLink = undefined,
}) => {
  const contentRef = React.createRef<HTMLDivElement>()

  return (
    <Container>
      <Header>
        <Subtitle>Filter Search By</Subtitle>
        <Title>Category</Title>
        <VerticalSpace height={20} mobileHeight={20} />
      </Header>
      <Content ref={contentRef}>
        {categories.map((category) => (
          <CardWrapper key={category.url}>
            <CategoryCard {...category} />
          </CardWrapper>
        ))}
        {galleryLink ? (
          <LaptopUpOnly>
            <CardWrapper>
              <CategoryCard
                url={galleryLink}
                image={galleryImage}
                title="Gallery"
              />
            </CardWrapper>
          </LaptopUpOnly>
        ) : (
          ""
        )}
        {clearanceLink ? (
          <LaptopUpOnly>
            <CardWrapper>
              <CategoryCard
                url={clearanceLink}
                image={clearanceImage}
                title="Clearance"
              />
            </CardWrapper>
          </LaptopUpOnly>
        ) : (
          ""
        )}
        <Spacer />
      </Content>
      <MobileOnly>
        <Scrollbar containerRef={contentRef} />
        <MobileSafariScrollbarHider />
      </MobileOnly>
    </Container>
  )
}

export default Categories
